import type { Phone } from "@tandemdiabetes/sugarmate-api-react-client"
import { usePromotePhone } from "@tandemdiabetes/sugarmate-api-react-client"
import { useMemo } from "react"
import { useAlerts } from "../alerts/useAlerts"
import { usePhones } from "./usePhones"
import { noticeError } from "src/utils"

export function usePromotePhoneToPrimary(
  // pass a callback that will be awaited before the swr state is mutated
  onSuccess?: (phoneId: Phone["id"]) => Promise<void>,
) {
  const promotePhone = usePromotePhone()
  const {
    mutate,
    data: phones,
  } = usePhones()
  const { mutate: mutateAlerts } = useAlerts()

  return useMemo(
    () => promotePhone && (async (id: Phone["id"]) => {
      try {
        const response = await promotePhone(id)

        try {
          await onSuccess?.(response.data.phone.id)
        } catch {
          // intentionally empty
        }

        if (phones) {
          const responsePhone = response.data.phone

          const updatedList = phones.map((cachedPhone) => {
            if (cachedPhone.id === responsePhone.id) {
              return responsePhone
            }

            return cachedPhone
          })

          // add the updated phone to the local state
          void mutate(updatedList)
        } else {
          void mutate()
        }

        // refresh local state with current alerts from server
        // promoting a phone can change what phones are assigned to alerts
        void mutateAlerts()
        return response
      } catch (e) {
        noticeError(
          e,
          "PromotePhone",
          { id },
        )

        throw e
      }
    }),
    [
      promotePhone,
      phones,
      mutate,
      mutateAlerts,
      onSuccess,
    ],
  )
}
